import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useExternalAI } from './useExternalAI';

// Локальное хранилище для состояния feedback
const STORAGE_KEY = 'chatwoot_ai_feedback_pending';

/**
 * Получить сохраненное состояние из localStorage
 */
const getSavedFeedbackState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Ошибка чтения состояния feedback:', error);
    return null;
  }
};

/**
 * Сохранить состояние в localStorage
 */
const saveFeedbackState = (state) => {
  try {
    if (state) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Ошибка сохранения состояния feedback:', error);
  }
};

// Глобальное reactive состояние (shared между всеми экземплярами)
const pendingFeedbackState = ref(getSavedFeedbackState());

/**
 * Composable для управления feedback AI-ассистента
 * Отслеживает некорректные ответы и ожидает правильный ответ от агента
 */
export function useAIFeedback() {
  const externalAI = useExternalAI();
  const currentChat = useMapGetter('getSelectedChat');

  /**
   * Проверяет, ожидается ли правильный ответ для текущего разговора
   */
  const isPendingFeedback = computed(() => {
    if (!pendingFeedbackState.value || !currentChat.value?.id) {
      return false;
    }
    return pendingFeedbackState.value.conversationId === currentChat.value.id;
  });

  /**
   * Получает некорректный ответ AI (если есть)
   */
  const incorrectAnswer = computed(() => {
    return isPendingFeedback.value ? pendingFeedbackState.value.incorrectAnswer : null;
  });

  /**
   * Устанавливает состояние "ожидание правильного ответа"
   * @param {string} conversationId - ID разговора
   * @param {string} incorrectAIAnswer - Некорректный ответ AI
   */
  const setPendingFeedback = (conversationId, incorrectAIAnswer) => {
    const state = {
      conversationId,
      incorrectAnswer: incorrectAIAnswer,
      timestamp: Date.now(),
    };
    pendingFeedbackState.value = state;
    saveFeedbackState(state);
  };

  /**
   * Отменяет ожидание правильного ответа
   */
  const cancelPendingFeedback = () => {
    pendingFeedbackState.value = null;
    saveFeedbackState(null);
  };

  /**
   * Отправляет feedback с правильным ответом
   * @param {string} correctAnswer - Правильный ответ от агента
   * @returns {Promise<boolean>} true если feedback отправлен успешно
   */
  const submitFeedback = async (correctAnswer) => {
    if (!isPendingFeedback.value) {
      return false;
    }

    const success = await externalAI.sendFeedback(
      incorrectAnswer.value,
      correctAnswer
    );

    if (success) {
      cancelPendingFeedback();
    }

    return success;
  };

  /**
   * Обрабатывает отправку сообщения агентом
   * Если есть ожидающий feedback, автоматически отправляет его
   * @param {string} messageContent - Содержимое сообщения
   * @returns {Promise<boolean>} true если feedback был отправлен
   */
  const handleAgentMessage = async (messageContent) => {
    if (isPendingFeedback.value && messageContent && messageContent.trim()) {
      return await submitFeedback(messageContent);
    }
    return false;
  };

  return {
    // Computed properties
    isPendingFeedback,
    incorrectAnswer,

    // Methods
    setPendingFeedback,
    cancelPendingFeedback,
    submitFeedback,
    handleAgentMessage,
  };
}

